/**
 * Persisted Class Scheduler UI preferences (last selected period, timeline chrome).
 * Survives reloads so the scheduler can reopen in the same place.
 */

const STORAGE_KEY = 'pi-schedule-class-scheduler-ui';
const LEGACY_TIMELINE_EXPANDED_KEY = 'pi-schedule-period-timeline-expanded';

const readState = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const writeState = (patch) => {
    try {
        const next = { ...readState(), ...patch };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
    } catch {
        return readState();
    }
};

const hasPeriodId = (periodId) => periodId != null && periodId !== '';

export const getClassSchedulerUiState = () => readState();

export const patchClassSchedulerUiState = (patch) => writeState(patch || {});

export const getLastSelectedPeriodId = () => {
    const periodId = readState().selectedPeriodId;
    return hasPeriodId(periodId) ? periodId : null;
};

export const setLastSelectedPeriodId = (periodId) => {
    if (!hasPeriodId(periodId)) return;
    writeState({ selectedPeriodId: periodId });
};

export const findPeriodById = (periods, periodId) => {
    if (!Array.isArray(periods) || !hasPeriodId(periodId)) {
        return null;
    }
    return periods.find((period) => String(period.id) === String(periodId)) ?? null;
};

export const pickInitialPeriod = (periods, savedPeriodId) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return null;
    }
    return findPeriodById(periods, savedPeriodId) || periods[0];
};

const readLegacyTimelineExpanded = () => {
    try {
        const stored = localStorage.getItem(LEGACY_TIMELINE_EXPANDED_KEY);
        if (stored === 'false') return false;
        if (stored === 'true') return true;
    } catch {
        // ignore storage errors
    }
    return null;
};

export const getTimelineExpanded = (defaultValue = true) => {
    const stored = readState().timelineExpanded;
    if (typeof stored === 'boolean') {
        return stored;
    }
    const legacy = readLegacyTimelineExpanded();
    return legacy == null ? defaultValue : legacy;
};

export const setTimelineExpanded = (isExpanded) => {
    writeState({ timelineExpanded: Boolean(isExpanded) });
};
